// The theme control, shared by every app so all three behave identically.
//
// Hand-written, not generated: this is logic, not data. React-free and
// dependency-free, because the three consumers are a Vite/React marketing
// site, a Vite/React iPad app and a Netlify-hosted booking system, and the one
// artifact all of them can take unchanged is a plain ES module.
//
// THE CONTRACT, and the reason for its shape:
//
//   data-theme       'light' | 'dark'            — the RESOLVED scheme. CSS reads only this.
//   data-theme-mode  'light' | 'dark' | 'system' — what the user CHOSE. CSS never reads it.
//
// `system` is resolved here in JavaScript and never in CSS, which is the one
// design decision worth explaining. An @media(prefers-color-scheme) rule looks
// simpler and is wrong for these apps: luxengine.com prerenders 19 static
// pages, so a CSS-resolved theme would freeze the BUILD MACHINE's OS
// preference into every one of them — a wrong answer that is invisible because
// nothing in the output says where it came from. Resolving here also keeps one
// source of truth: the toggle's own label and the mobile theme-colour both
// need the resolved value anyway, and two resolution paths that must agree is
// a bug waiting to happen. It is testable in jsdom, which does not evaluate
// @media in getComputedStyle.

/** Where the chosen mode is kept. One key across every app on the domain. */
export const THEME_STORAGE_KEY = 'luxengine-theme';

/** The order a single cycling button walks. system first, so the default
 *  state leads and one more press always returns to it. */
export const THEME_MODES = ['system', 'light', 'dark'];

const QUERY = '(prefers-color-scheme: dark)';

function media() {
  return typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia(QUERY)
    : null;
}

/** What the operating system is asking for right now.
 *
 *  Defaults to 'light' when it cannot be determined, matching the CSS: the
 *  kit's bare blocks carry light, so an app with no signal renders the same
 *  thing whether this returns early or the stylesheet answers alone. */
export function systemScheme() {
  return media()?.matches ? 'dark' : 'light';
}

export function isMode(value) {
  return THEME_MODES.includes(value);
}

/** The mode the user chose, or `fallback` when they have not chosen.
 *
 *  'system' is stored explicitly rather than as an absent key. Deleting the
 *  key to mean "auto" makes "never chose" and "chose system" the same state,
 *  which is fine until an app wants a different default for a first visit. */
export function getMode(fallback = 'system', storage) {
  const store = storage ?? safeStorage();
  try {
    const saved = store?.getItem(THEME_STORAGE_KEY);
    return isMode(saved) ? saved : fallback;
  } catch {
    return fallback;
  }
}

export function setMode(mode, storage) {
  const store = storage ?? safeStorage();
  try {
    if (isMode(mode)) store?.setItem(THEME_STORAGE_KEY, mode);
  } catch {
    // Private browsing, or storage disabled. The theme still works for this
    // page; it just will not be remembered.
  }
  return resolveMode(mode);
}

/** The scheme a mode resolves to now. */
export function resolveMode(mode) {
  return mode === 'light' || mode === 'dark' ? mode : systemScheme();
}

/** The next mode for a single cycling button. */
export function nextMode(mode) {
  const i = THEME_MODES.indexOf(mode);
  return THEME_MODES[(i + 1) % THEME_MODES.length];
}

/**
 * Paint a scheme onto the document.
 *
 * `color-scheme` is set as well as the attribute, which is what makes form
 * controls, scrollbars and the caret follow the page instead of staying in the
 * browser's default light. It is the single most commonly missed line in a
 * dark-mode implementation.
 */
export function applyScheme(scheme, options = {}) {
  const root = options.root ?? globalThis.document?.documentElement;
  if (!root) return scheme;
  root.setAttribute('data-theme', scheme);
  root.style.colorScheme = scheme;
  if (options.mode) root.setAttribute('data-theme-mode', options.mode);

  // Keep the mobile browser chrome with the page. Read back out of the
  // cascade rather than passed in, so it cannot become another place the
  // palette is written down.
  const doc = root.ownerDocument ?? globalThis.document;
  const meta = doc?.querySelector('meta[name="theme-color"]');
  if (meta && doc?.defaultView) {
    const paper = doc.defaultView
      .getComputedStyle(root)
      .getPropertyValue(options.themeColorProperty ?? '--color-paper')
      .trim();
    if (paper) meta.setAttribute('content', paper);
  }
  return scheme;
}

/**
 * Wire the whole thing up.
 *
 * Returns a function that both unsubscribes and is safe to call twice, so a
 * React effect can hand it straight back as its cleanup.
 */
export function initTheme(options = {}) {
  const mode = options.mode ?? getMode(options.defaultMode ?? 'system');
  const paint = () => applyScheme(resolveMode(mode), { ...options, mode });
  paint();
  options.onChange?.(resolveMode(mode), mode);

  // A media query emits a change event, so nothing polls. The clock-based
  // 'auto' this replaces needed a 60-second interval and could still be up to
  // a minute stale.
  if (mode !== 'system') return () => {};
  const mq = media();
  if (!mq) return () => {};
  const listener = () => {
    paint();
    options.onChange?.(resolveMode(mode), mode);
  };
  mq.addEventListener('change', listener);
  return () => mq.removeEventListener('change', listener);
}

function safeStorage() {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

/**
 * The script to run BEFORE first paint, inlined in <head>.
 *
 * Without it the page paints its default scheme, then React mounts and
 * repaints — a white flash on every load for a dark-mode visitor. It has to be
 * inline and synchronous, so it is a string rather than a module, and it
 * duplicates a few lines of the logic above on purpose: an import here would
 * be a network round trip in the one place that cannot afford one.
 */
export const NO_FLASH_SNIPPET = `(function(){try{
var k=${JSON.stringify(THEME_STORAGE_KEY)},m=localStorage.getItem(k);
if(m!=='light'&&m!=='dark'&&m!=='system')m=document.documentElement.getAttribute('data-theme-mode')||'system';
var s=m==='light'||m==='dark'?m:(window.matchMedia&&window.matchMedia('${QUERY}').matches?'dark':'light');
var r=document.documentElement;r.setAttribute('data-theme',s);r.setAttribute('data-theme-mode',m);r.style.colorScheme=s;
}catch(e){}})();`;
